import { useMemo } from 'react';
import { SVGS } from '../../../assets';
import { TouchableOpacity } from '../../../common';
import { getFormattedAmount } from '@zonofi/common';

interface AmountInputCardProps {
  amountType: 'percentage' | 'amount';
  amountValue: string;
  handleTypeSelect: (type: 'percentage' | 'amount') => void;
  dropdownOpen: boolean;
  handleDropdownToggle: () => void;
  onChange: (value: string) => void;
  paymentTermsAndConditions: string;
  onPaymentTermsChange: (value: string) => void;
  disabled?: boolean;
  advancePaymentAmount?: number;
}

const PERCENT_PRESETS = [5, 10, 20, 50];
const FIXED_PRESETS = [500, 1000, 1500, 2000];
export const AmountInputCard: React.FC<AmountInputCardProps> = ({
  amountType,
  amountValue,
  handleTypeSelect,
  dropdownOpen,
  handleDropdownToggle,
  onChange,
  paymentTermsAndConditions,
  onPaymentTermsChange,
  disabled = false,
  advancePaymentAmount,
}) => {
  const presetsData = useMemo(
    () => (amountType === 'percentage' ? PERCENT_PRESETS : FIXED_PRESETS),
    [amountType]
  );

  const dropdownData = useMemo(
    () => [
      {
        key: 'percentage',
        lable: 'Percentage (%)',
        onPress: () => handleTypeSelect('percentage'),
        active: amountType === 'percentage',
      },
      {
        key: 'amount',
        lable: 'Amount (₹)',
        onPress: () => handleTypeSelect('amount'),
        active: amountType === 'amount',
      },
    ],
    [amountType, handleTypeSelect]
  );

  return (
    <div className={`check-amount-box${disabled ? ' disabled' : ''}`}>
      <div className="check-amount-box-header font-semibold">
        {'Advance percentage or amount'}
      </div>
      <div className="check-amount-box-input-view">
        <div className="check-amount-box-toggle">
          <TouchableOpacity
            className="check-amount-box-toggle-btn"
            onPress={handleDropdownToggle}
          >
            <span className="check-amount-box-toggle-text font-regular">
              {amountType === 'percentage' ? 'Percentage (%)' : 'Amount (₹)'}
            </span>
            <img
              src={SVGS.ArrowDownBlack}
              className={`check-amount-box-toggle-chevron${dropdownOpen ? ' open' : ''}`}
            />
          </TouchableOpacity>

          {dropdownOpen && (
            <div className="check-amount-box-dropdown">
              {dropdownData.map((item) => (
                <TouchableOpacity
                  key={item.key}
                  className={`check-amount-box-dropdown-item${item.active ? ' active' : ''}`}
                  onPress={item.onPress}
                >
                  <span className="check-amount-box-dropdown-text font-regular">
                    {item?.lable}
                  </span>
                </TouchableOpacity>
              ))}
            </div>
          )}
        </div>

        <div className="check-amount-box-divider" />

        <input
          className="check-amount-box-input font-regular"
          type="number"
          min={0}
          max={amountType === 'percentage' ? 100 : undefined}
          value={amountValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={'00'}
          inputMode="numeric"
        />
      </div>

      <div className="check-amount-box-persest-view">
        {presetsData.map((p) => (
          <TouchableOpacity
            key={p}
            className={`check-amount-box-persest-view-chip${
              amountValue === String(p) ? ' selected' : ''
            } font-regular`}
            onPress={() => onChange(String(p))}
          >
            {amountType === 'percentage' ? `${p}%` : `₹${p}`}
          </TouchableOpacity>
        ))}
      </div>

      {amountType === 'percentage' && amountValue && advancePaymentAmount ? (
        <div className="check-amount-box-footer">
          <span className="check-amount-box-footer-text font-regular">
            {`Customer must pay`}{' '}
            <span className="font-semibold">
              {getFormattedAmount(advancePaymentAmount)}
            </span>{' '}
            {`in advance for this order.`}
          </span>
        </div>
      ) : null}

      <div className="check-amount-box-header column-gap font-semibold">
        {'Payment Terms & Conditions'}
      </div>
      <textarea
        value={paymentTermsAndConditions}
        onChange={(e) => onPaymentTermsChange(e.target.value)}
        className="check-amount-box-text-area font-regular"
        style={{ overflow: 'hidden', resize: 'none' }}
        placeholder="Enter terms and conditions for the order"
      />
    </div>
  );
};
